/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ClockIcon from '@material-ui/icons/AccessTime';
import {
  DateRange,
  DateRangeDelimiter,
  DateTimePicker,
  LocalizationProvider,
  StaticDateRangePicker
} from '@material-ui/pickers';
import MomentUtils from '@material-ui/pickers/adapter/moment';
import moment, {Moment} from 'moment';
import {translate, WhitelistedLocales} from '@ion/core/src/locale/locale';
import {isFutureDay, isValidDateTime} from '@ion/core/src/utils/DateUtil';
import {
  DatePicker,
  DatePickerTextConfig
} from '../../editors/datetimepicker/DatePicker';
import useMedia from '../../hooks/useMedia';
import {dlsColor, dlsZIndex, getStyleClass} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme, LastFilteredDateProps} from '../../types/types';
import TextField from '../textfield/TextField';
import CustomToolbar from './CustomToolbar';
import textFieldOverrides from './textFieldOverrides';

export interface StaticDateRangeTimePickerProps extends DatePickerTextConfig {
  locale?: string;
  startInputLabel?: string;
  endInputLabel?: string;
  readOnly?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  onRangeChange?: (val: Moment[]) => void;
  onStartDateChange?: (val: Moment) => void;
  onEndDateChange?: (val: Moment) => void;
  setDateError?: Function;
  minDate?: Moment;
  maxDate?: Moment;
  maxHVSAssetDays?: number;
  inputFormat?: string;
  errorMessage?: string;
  customRange?: boolean;
  hasError?: boolean;
  isDialogPicker?: boolean;
  defaultStartDate?: Date | string;
  defaultEndDate?: Date | string;
  customError?: boolean;
  lastFilteredDateProps?: LastFilteredDateProps;
  hideClearButton?: boolean;
};

enum DateType {
  start = 'start',
  end = 'end'
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  dateRangeWrapper : {
    width                          : '100%',
    display                        : 'flex',
    maxWidth                       : '1200px',
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column'
    }
  },
  customDateRangeWrapper : {
    width                          : '100%',
    display                        : 'table-cell',
    maxWidth                       : '1200px',
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column'
    }
  },
  customInputsWrapper : {
    width     : '100%',
    display   : 'flex',
    columnGap : '50px'
  },
  inputsWrapper : {
    padding                        : '20px 50px 30px 0px',
    width                          : '30%',
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  input : {
    marginBottom : '40px'
  },
  calendarPicker : {
    '& button.Mui-disabled' : {
      color : dlsColor('grey4')
    }
  },
  'current-calendarPicker' : {
    '& .Mui-disabled' : {
      color : `${dlsColor('grey4')} !important`
    },
    '& .MuiPickersDay-root' : {
      color         : dlsColor('grey1'),
      width         : 40,
      height        : 40,
      fontSize      : '16px',
      fontFamily    : 'Inter',
      fontWeight    : 400,
      lineHeight    : '24px',
      letterSpacing : '1px'
    },
    '& .MuiPickersDay-today' : {
      border : `1px solid ${dlsColor('darkTeal')} !important`
    },
    '& .MuiPickersDesktopDateRangeCalendar-arrowSwitcher h6' : {
      color         : dlsColor('grey1'),
      fontSize      : '16px',
      fontFamily    : 'Inter',
      fontWeight    : 700,
      lineHeight    : '24px',
      letterSpacing : '1px',
      textTransform : 'uppercase'
    },
    '& .MuiPickersCalendar-daysHeader .MuiPickersCalendar-weekDayLabel' : {
      color         : dlsColor('grey1'),
      fontSize      : '12px',
      fontFamily    : 'Inter',
      fontWeight    : 500,
      lineHeight    : '16px',
      letterSpacing : '0.4px'
    },
    '& .MuiPickersDateRangeDay-rangeIntervalDayHighlight' : {
      height       : '40px',
      display      : 'flex',
      marginTop    : 0,
      alignItems   : 'center',
      background   : `${dlsColor('paleBlue')} !important`,
      borderRadius : '20px !important'
    },
    '& .MuiPickersDateRangeDay-rangeIntervalDayHighlight .MuiPickersDay-root' : {
      color          : `${dlsColor('darkTeal')} !important`,
      display        : 'inline-flex',
      alignItems     : 'center',
      fontWeight     : 400,
      fontFamily     : 'Inter',
      lineHeight     : '24px',
      letterSpacing  : '1px',
      justifyContent : 'center'
    },
    '& .MuiPickersDay-root.Mui-selected' : {
      color           : `${dlsColor('darkTeal')} !important`,
      fontWeight      : 400,
      fontFamily      : 'Inter',
      lineHeight      : '24px',
      letterSpacing   : '1px',
      borderRadius    : '20px !important',
      backgroundColor : `${dlsColor('paleBlue')} !important`
    },
    '& .MuiPickersDay-root.Mui-selected:hover' : {
      backgroundColor : `${dlsColor('paleBlue2')} !important`
    },
    '& .MuiPickersDateRangeDay-rangeIntervalDayHighlightStart' : {
      marginLeft             : 0,
      borderTopLeftRadius    : '20px !important',
      borderBottomLeftRadius : '20px !important'
    },
    '& .MuiPickersDateRangeDay-rangeIntervalDayHighlightEnd' : {
      marginRight             : 0,
      borderTopRightRadius    : '20px !important',
      borderBottomRightRadius : '20px !important'
    },
    '& .MuiPickersDay-root:focus.Mui-selected' : {
      backgroundColor : `${dlsColor('paleBlue2')} !important`
    }
  },
  popper : {
    zIndex                                    : dlsZIndex('staticDateRangeTimePickerPopper') as any,
    '& div.MuiDateTimePickerTabs-modeDesktop' : {
      display : 'none'
    },
    '& div.MuiDateTimePickerTabs-root' : {
      display : 'none'
    },
    '& div.MuiPickersToolbar-root' : {
      display : 'none'
    }
  },
  'current-popper' : {
    '& .MuiPickersPopper-paper' : {
      gap             : '20px',
      display         : 'flex',
      opacity         : 1,
      overflow        : 'hidden',
      borderTop       : `2px solid ${dlsColor('pastelTeal')} !important`,
      boxShadow       : '0px 4px 4px 0px #00000040 !important',
      paddingTop      : '16px',
      borderRadius    : '12px !important',
      flexDirection   : 'column',
      backgroundColor : `${dlsColor('white')} !important`
    },
    '& .MuiPickersClock-amButton, & .MuiPickersClock-pmButton' : {
      display : 'none !important'
    },
    '& .MuiPickersArrowSwitcher-root, & .MuiIconButton-edgeEnd, & .MuiIconButton-edgeStart' : {
      display : 'none !important'
    },
    '& div.MuiDateTimePickerTabs-modeDesktop' : {
      display : 'none'
    },
    '& div.MuiDateTimePickerTabs-root' : {
      display : 'none'
    },
    '& div.MuiPickersToolbar-root' : {
      display : 'none !important'
    },
    '& .MuiPickersClockNumber-root' : {
      color         : `${dlsColor('grey1')} !important`,
      fontSize      : '16px !important',
      textAlign     : 'center',
      fontFamily    : 'Inter',
      fontWeight    : '400 !important',
      lineHeight    : '22px !important',
      letterSpacing : '0px !important',
      verticalAlign : 'middle'
    },
    '& .MuiPickersClockNumber-clockNumberSelected' : {
      color           : `${dlsColor('white')} !important`,
      backgroundColor : dlsColor('darkTeal')
    },
    '& .MuiPickersClock-squareMask' : {
      borderRadius    : '500px',
      backgroundColor : dlsColor('grey8')
    },
    '& .MuiPickersClockPointer-pointer' : {
      backgroundColor : dlsColor('darkTeal')
    },
    '& .MuiPickersClock-pin' : {
      backgroundColor : dlsColor('darkTeal')
    },
    '& .MuiPickersClockPointer-thumb' : {
      border : `14px solid ${dlsColor('darkTeal')}`
    },
    zIndex : dlsZIndex('staticDateRangeTimePickerPopper') as any
  },
  'current-cta' : {
    color         : dlsColor('darkestBlue'),
    cursor        : 'pointer',
    fontSize      : '13px',
    fontFamily    : 'Inter',
    fontWeight    : 600,
    lineHeight    : '16px',
    letterSpacing : '1px',
    verticalAlign : 'middle'
  },
  error : {
    fontSize   : '12px',
    textAlign  : 'left',
    fontFamily : 'Roboto',
    color      : dlsColor('caRed'),
    lineHeight : '0px',
    position   : 'relative',
    bottom     : '18px'
  }
}));

/**
 * @StaticDateRangeTimePicker
 *
 * Date range picker that is static, meaning it's always rendered and not launched in
 * a modal. Has an AirBnb style  date range picker on a calendar with inputs that allow
 * the time to be adjusted as the calendar defaults the time to 12 AM.
 *
 * ```typescript
 *
 *  import {StaticDateRangeTimePicker} from '@ion/dls/src/components/date/StaticDateRangeTimePicker';
 *
 *  <StaticDateRangeTimePicker disableFuture
 *    onRangeChange={onRangeChange}
 *    onStartDateChange={onStartChange}
 *    onEndDateChange={onEndChange} />
 *
 * ```
 *
 * @param {string} locale: locale so that calendar is displayed in local language
 * @param {string} startInputLabel: label for start date input
 * @param {string} endInputLabel: label for end date input
 * @param {boolean} readOnly: will disable the calendar and inputs
 * @param {boolean} disablePast: past dates from today will be disabled in calendar
 * @param {boolean} disableFuture: future dates from today will be disabled in calendar
 * @param {Function} onRangeChange: fires when user changes a date on the calendar
 * @param {Function} onStartDateChange: fires when user selects a start date on
 *        the calendar OR changes the start date input value
 * @param {Function} onEndDateChange: fires when user selects an end date on
 *        the calendar OR changes the end date input value
 * @param {Function} setDateError: called when the error of the date range picker
 *        needs to be changed
 */
export const StaticDateRangeTimePicker = ({
  locale = WhitelistedLocales.en,
  startInputLabel = '',
  endInputLabel = '',
  readOnly = false,
  disablePast = false,
  disableFuture = false,
  customRange = false,
  isDialogPicker = false,
  customError = false,
  inputFormat = 'M/D/YYYY h:mm A',
  onRangeChange,
  onStartDateChange,
  onEndDateChange,
  setDateError,
  maxDate,
  maxHVSAssetDays,
  hasError,
  errorMessage,
  defaultStartDate,
  defaultEndDate,
  lastFilteredDateProps,
  minDate = moment(new Date('1900-01-01')),
  useDatePickerTextField = false,
  hideClearButton = false
}: StaticDateRangeTimePickerProps) => {
  const {
    startDate,
    endDate
  } = lastFilteredDateProps || {};

  const classes                             = useStyles();
  const [startValue, setStartValue]         = useState<Moment>(startDate || null);
  const [endValue, setEndValue]             = useState<Moment>(endDate || null);
  const [value, setValue]                   = useState<DateRange<Moment>>([startValue, endValue]);
  const [endDateError, setEndDateError]     = useState(null);
  const [startDateError, setStartDateError] = useState(null);
  const [rangeError, setRangeError]         = useState(null);
  const {isPhone, isTablet}                 = useMedia();
  const {isCurrent: isCurrentTheme}         = useThemeContext();

  useEffect(() => {
    checkInvalidDateRange(startDate, endDate);
    startDate && endDate && setValue([startDate, endDate]);
  }, []);

  const getClass = useCallback((name: string) => (
    getStyleClass(name, classes, isCurrentTheme)
  ), [isCurrentTheme, classes]);

  /**
   * @private
   * updateTime updates {hour : min} value received from the dateRange picker
   * with the lastly selected {hour : min} by user.
   * If !endValue, it will set the {hour : min} to 23:59PM
   */
  const updateTime = useCallback((value: Moment, dateType: String): void => {
    if (value) {
      const ref = dateType === DateType.start ? startValue : endValue;
      const hour = moment(ref).hours();
      const min  = moment(ref).minutes();
      value.set({h: hour, m: min});
    };
  }, [startValue, endValue]);

  function handleChange(val: Moment[]) {
    const val1 = val[0] || startValue;
    const val2 = val[1] || endValue;

    updateTime(val1, DateType.start);
    updateTime(val2, DateType.end);

    checkInvalidDateRange(val1, val2);

    setValue([val1, val2]);
    onRangeChange?.([val1, val2]);

    setStartValue(val1);
    onStartDateChange?.(val1);

    setEndValue(val2);
    onEndDateChange?.(val2);
  }

  function handleStartTimeChange(val: Moment) {
    setStartValue(val);
    onStartDateChange?.(val);
    const toDate = value[1];
    setValue([val, toDate]);
    onRangeChange?.([val, toDate]);
    checkInvalidDateRange(val, toDate);
  }

  function handleEndTimeChange(val: Moment) {
    setEndValue(val);
    onEndDateChange?.(val);
    const fromDate = value[0] || startValue;
    setValue([fromDate, val]);
    onRangeChange?.([fromDate, val]);
    checkInvalidDateRange(fromDate, val);
  }

  function checkInvalidDateRange(startDate: Moment, endDate: Moment) {
    if (!startDate || !endDate) {
      setRangeError(null);
      return setEndDateError(null);
    };
    if (maxHVSAssetDays) {
      const diff = isDialogPicker ? moment(endDate).diff(moment(startDate), 'days') : endDate.diff(startDate, 'days');
      setRangeError(
        diff >= maxHVSAssetDays
          ? translate('common:time.maxRangeError', {maxRange: maxHVSAssetDays})
          : null
      );
    }
    // start date and end date validation...
    const isValidStartDate  = isValidDateTime(startDate, inputFormat);
    const isValidEndDate    = isValidDateTime(endDate, inputFormat);
    const isStartDateFuture = isFutureDay(startDate);
    const isEndDateFuture   = isFutureDay(endDate);
    const startDateYear     = moment(startDate).year();
    const endDateYear       = moment(endDate).year();
    if (!isValidStartDate || startDateYear < minDate.year()) {
      setStartDateError(translate('common:time.formatError'));
    } else if (disableFuture && isStartDateFuture) {
      setStartDateError(translate('common:time.futureDayError'));
    } else {
      setStartDateError(null);
    }
    if (!isValidEndDate || endDateYear < minDate.year()) {
      setEndDateError(translate('common:time.formatError'));
    } else if (disableFuture && isEndDateFuture) {
      setEndDateError(translate('common:time.futureDayError'));
    } else if (isValidStartDate && isValidEndDate && endDate < startDate) {
      setEndDateError(translate('common:time.endDateBeforeStart'));
    } else {
      setEndDateError(null);
    }
  }

  useEffect(() => {
    const error = Boolean(
      startDateError ||
      endDateError ||
      rangeError
    );
    setDateError?.(error);
  }, [startDateError, endDateError, rangeError]);

  return (
    <LocalizationProvider dateAdapter={MomentUtils} locale={locale}>
      <div className={classes[customRange ? 'customDateRangeWrapper' : 'dateRangeWrapper']}>
        <div className={ classes[customRange ? 'customInputsWrapper' : 'inputsWrapper']}>
          <div className={classes.input}>
            {isDialogPicker ? <DatePicker isDateTime closeOnClickOutside
              required
              customError={customError}
              errorText={errorMessage}
              isClock={true}
              isDatePicker={true}
              useDatePickerTextField={useDatePickerTextField}
              defaultValue={defaultStartDate}
              disabled={readOnly}
              hideClearButton={hideClearButton}
              onChange={handleStartTimeChange} /> : <DateTimePicker ampm
              clearable
              allowKeyboardControl
              openPickerIcon={<ClockIcon />}
              openTo='hours'
              inputFormat={inputFormat}
              disabled={readOnly}
              renderInput={function(props: any) {
                const {
                  error,
                  helperText,
                  ...rest
                } = props;
                delete rest?.inputProps?.placeholder;
                return (
                  <TextField error={!!startDateError}
                    styleOverrides={textFieldOverrides}
                    fieldLabel={startInputLabel}
                    placeholder='mm/dd/yyyy hh:mm (am|pm)'
                    helperText={startDateError}
                    {...rest} />
                );
              }}
              value={startValue}
              onChange={handleStartTimeChange}
              ToolbarComponent={CustomToolbar}
              showToolbar={isCurrentTheme}
              PopperProps={{className: getClass('popper')}}
              DialogProps={{className: getClass('popper')}} />}
          </div>
          <div>
            {isDialogPicker ? <DatePicker isDateTime closeOnClickOutside
              required
              customError={customError}
              errorText={endDateError || errorMessage}
              hasError={endDateError || rangeError}
              isClock={true}
              isDatePicker={true}
              useDatePickerTextField={useDatePickerTextField}
              defaultValue={defaultEndDate}
              disabled={readOnly}
              hideClearButton={hideClearButton}
              onChange={handleEndTimeChange} /> : <DateTimePicker ampm
              clearable
              openPickerIcon={<ClockIcon />}
              openTo='hours'
              inputFormat={inputFormat}
              disabled={readOnly}
              renderInput={function(props: any) {
                const {
                  error,
                  helperText,
                  ...rest
                } = props;
                delete rest?.inputProps?.placeholder;
                return (
                  <TextField error={endDateError || rangeError || error}
                    styleOverrides={textFieldOverrides}
                    fieldLabel={endInputLabel}
                    placeholder='mm/dd/yyyy hh:mm (am|pm)'
                    helperText={endDateError || rangeError}
                    {...rest} />
                );
              }}
              value={endValue}
              onChange={handleEndTimeChange}
              ToolbarComponent={CustomToolbar}
              showToolbar={isCurrentTheme}
              PopperProps={{className: getClass('popper')}}
              DialogProps={{className: getClass('popper')}} />}
          </div>
        </div>
        {hasError &&
          <div className={classes.error}>
            {errorMessage}
          </div>}
        {!isDialogPicker && <StaticDateRangePicker allowSameDateSelection
          showDaysOutsideCurrentMonth
          showToolbar={false}
          className={getClass('calendarPicker')}
          readOnly={readOnly}
          disablePast={disablePast}
          disableFuture={disableFuture}
          minDate={minDate}
          maxDate={maxDate}
          displayStaticWrapperAs={isTablet ? 'mobile' : 'desktop'}
          value={value}
          onChange={handleChange}
          renderInput={function(startProps, endProps) {
            return (isPhone ? null : (
              <>
                <TextField {...startProps} />
                <DateRangeDelimiter> to </DateRangeDelimiter>
                <TextField {...endProps} />
              </>
            ));
          }} />}
      </div>
    </LocalizationProvider>
  );
};

export default StaticDateRangeTimePicker;
